import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, ArrowRight } from "lucide-react";

const primaryCities = [
  {
    name: "Dallas",
    neighborhoods: ["Uptown", "Deep Ellum", "Oak Cliff", "Bishop Arts", "Design District", "Lakewood", "Addison", "Preston Hollow", "Victory Park"],
    county: "Dallas County",
  },
  {
    name: "Fort Worth",
    neighborhoods: ["Downtown Fort Worth", "Sundance Square", "Near Southside", "Cultural District", "TCU Area", "West 7th"],
    county: "Tarrant County",
  },
  {
    name: "Plano",
    neighborhoods: ["Legacy West", "Downtown Plano", "West Plano", "East Plano"],
    county: "Collin County",
  },
  {
    name: "Frisco",
    neighborhoods: ["The Star District", "Stonebriar", "Frisco Square", "West Frisco"],
    county: "Collin County",
  },
  {
    name: "Arlington",
    neighborhoods: ["Entertainment District", "UT Arlington", "Downtown Arlington", "South Arlington"],
    county: "Tarrant County",
  },
  {
    name: "Irving",
    neighborhoods: ["Las Colinas", "Valley Ranch", "North Irving"],
    county: "Dallas County",
  },
];

const allCities = [
  "Allen", "Carrollton", "Cedar Hill", "Coppell", "Corsicana",
  "DeSoto", "Denton", "Duncanville", "Euless", "Flower Mound",
  "Garland", "Grand Prairie", "Grapevine", "Hurst", "Keller",
  "Lancaster", "Lewisville", "Little Elm", "Mansfield", "McKinney",
  "Mesquite", "Midlothian", "Murphy", "North Richland Hills",
  "Princeton", "Prosper", "Richardson", "Rockwall", "Rowlett",
  "Sachse", "Southlake", "The Colony", "Waxahachie", "Weatherford",
  "Wylie",
];

export default function ServiceArea() {
  const areaSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Custom Screen Printing Delivery — DFW Metroplex",
    "description": "Free custom apparel delivery throughout the Dallas-Fort Worth Metroplex. Screen printing, DTF, embroidery delivered to your door.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Ninja Screen Print",
      "address": { "@type": "PostalAddress", "addressLocality": "Dallas", "addressRegion": "TX" }
    },
    "areaServed": [
      "Dallas, TX", "Fort Worth, TX", "Plano, TX", "Frisco, TX",
      "Arlington, TX", "Irving, TX", "Garland, TX", "McKinney, TX",
      "Allen, TX", "Richardson, TX", "Carrollton, TX", "Lewisville, TX"
    ]
  };

  return (
    <>
      <Helmet>
        <title>DFW Screen Printing Service Area | Dallas Custom Shirts Delivery | Ninja Screen Print</title>
        <meta name="description" content="Ninja Screen Print serves all of the DFW Metroplex with free custom apparel delivery. Dallas, Fort Worth, Plano, Frisco, Arlington, Irving and 30+ more DFW cities." />
        <script type="application/ld+json">{JSON.stringify(areaSchema)}</script>
      </Helmet>

      {/* Hero */}
      <div
        className="pt-16 relative min-h-[40vh] flex items-end pb-12 overflow-hidden"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1765611800420-92b96b438fa3?w=1400&q=80')", backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="relative z-10 ninja-container">
          <div className="red-line" />
          <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">
            DFW Service Area
          </h1>
          <p className="text-neutral-300 font-inter mt-3 text-lg max-w-2xl">
            Ninja Screen Print delivers custom apparel free to your door throughout the entire Dallas-Fort Worth Metroplex. No shipping. No meetups. Just great shirts, delivered professionally.
          </p>
        </div>
      </div>

      {/* Delivery Info */}
      <div className="bg-red-700 py-4" data-testid="delivery-banner">
        <div className="ninja-container flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-white" />
            <span className="text-white font-inter text-sm">Free local delivery on all orders of <strong>24+ pieces</strong> anywhere in DFW</span>
          </div>
          <Link to="/contact" className="text-white border border-white/50 hover:bg-white hover:text-red-700 px-5 py-1.5 text-xs font-inter uppercase tracking-widest transition-colors whitespace-nowrap">
            GET A QUOTE
          </Link>
        </div>
      </div>

      {/* Primary Cities */}
      <section className="ninja-section" data-testid="primary-cities">
        <div className="ninja-container">
          <div className="mb-10">
            <div className="red-line" />
            <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight">Primary Service Markets</h2>
            <p className="text-neutral-400 font-inter mt-2">Our highest-frequency delivery zones — same-day delivery available for rush orders in these areas.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {primaryCities.map((city) => (
              <div key={city.name} data-testid={`city-${city.name.toLowerCase()}`} className="ninja-card p-6">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={14} className="text-red-500" />
                  <span className="text-xs text-neutral-500 uppercase tracking-widest font-inter">{city.county}</span>
                </div>
                <h3 className="font-teko text-2xl text-white uppercase tracking-wide mb-3">
                  Screen Printing {city.name}
                </h3>
                <div className="flex flex-wrap gap-1 mb-4">
                  {city.neighborhoods.map((n) => (
                    <span key={n} className="text-xs text-neutral-400 border border-neutral-800 px-2 py-0.5 font-inter">{n}</span>
                  ))}
                </div>
                <Link to="/contact" className="flex items-center gap-1 text-xs text-red-500 hover:text-red-400 transition-colors font-inter uppercase tracking-widest">
                  Order for {city.name} <ArrowRight size={10} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All DFW Cities */}
      <section className="ninja-section bg-[#0d0d0d]" data-testid="all-cities">
        <div className="ninja-container">
          <div className="mb-8">
            <div className="red-line" />
            <h2 className="font-teko text-4xl text-white uppercase tracking-tight">All DFW Delivery Areas</h2>
            <p className="text-neutral-400 font-inter mt-2">We deliver custom apparel throughout the entire DFW Metroplex — including these cities and more:</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {allCities.map((city) => (
              <div key={city} className="text-center ninja-card p-3 hover:border-red-600/40 transition-colors">
                <div className="text-xs text-neutral-400 font-inter">{city}, TX</div>
              </div>
            ))}
          </div>
          <p className="text-neutral-600 font-inter text-xs mt-6">
            Don't see your city? We likely serve it. Contact us to confirm — most Texas destinations within 100 miles of Dallas qualify for free delivery on 24+ pieces.
          </p>
        </div>
      </section>

      {/* SEO Area Descriptions */}
      <section className="ninja-section" data-testid="area-seo">
        <div className="ninja-container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { city: "Plano", text: "Custom screen printing in Plano TX for tech companies, Legacy West businesses, school spirit wear, and sports teams. Free delivery to Plano." },
              { city: "Frisco", text: "Screen printing in Frisco TX for The Star District businesses, Frisco ISD events, sports tournaments, and local restaurants. Free delivery to Frisco." },
              { city: "McKinney", text: "Custom shirts in McKinney TX for downtown events, local businesses, church apparel, and sports teams. Free Ninja Print delivery to McKinney." },
              { city: "Arlington", text: "Screen printing in Arlington TX near UT Arlington, Entertainment District, and Six Flags. Custom event shirts and corporate apparel delivered free." },
              { city: "Irving", text: "Custom embroidery and screen printing in Irving TX for Las Colinas businesses, DFW Airport hotels, and Valley Ranch corporate clients." },
              { city: "Richardson", text: "Custom apparel in Richardson TX for UTD students, Telecom Corridor businesses, school events, and local sports leagues. Free delivery." },
            ].map((item) => (
              <div key={item.city} className="ninja-card p-5">
                <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-2">
                  Screen Printing {item.city}, TX
                </h3>
                <p className="text-neutral-500 font-inter text-xs leading-relaxed">{item.text}</p>
                <Link to="/contact" className="mt-3 inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-400 font-inter uppercase tracking-widest">
                  Quote for {item.city} <ArrowRight size={10} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0d0d0d] border-t border-neutral-800">
        <div className="ninja-container text-center">
          <h2 className="font-teko text-4xl text-white uppercase tracking-tight">Order Custom Apparel for Your DFW Business</h2>
          <p className="text-neutral-400 font-inter mt-3 max-w-xl mx-auto">Free quote in 2 hours. Free delivery on 24+ pieces. Anywhere in the DFW Metroplex.</p>
          <Link to="/contact" data-testid="area-cta-btn" className="ninja-btn-primary font-teko tracking-widest text-lg px-10 py-4 mt-6 inline-block">GET A FREE QUOTE</Link>
        </div>
      </section>
    </>
  );
}
